import {
  CREATE_SERVICE_CASE,
  CREATE_SERVICE_CASE_SET_SUBMITTING,
  CREATE_SERVICE_CASE_UNSET_SUBMITTING,
} from 'actions';
import Notifier from 'common/helpers/Notifier';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import { coreApi } from 'src/api/config';
import { crtb2130MultipleClientNeeds } from 'src/common/utils/FeatureFlags/flags';
import { feeSchedulePrograms as fspUtils } from '@unite-us/client-utils';
import { datadogLogs } from '@datadog/browser-logs';

const iso8601Date = (date) => moment.unix(date).toISOString();

const isPresent = (value) => value !== null && value !== undefined && value !== '';

const parseAuthorizationParams = (params) => {
  const authorizationRequest = get(params, 'authorization_request', {});
  const serviceStart = get(authorizationRequest, 'service_start', null);
  const amountRequested = get(authorizationRequest, 'amount_requested', null);

  if (!isPresent(serviceStart) || !isPresent(amountRequested)) {
    return {};
  }

  return authorizationRequest;
};

const getCaseState = (isOON, params) => {
  if (!isEmpty(parseAuthorizationParams(params))) {
    return 'pending_authorization';
  }
  return isOON ? 'off_platform' : 'managed';
};

const buildPersonConditions = (clientNeedIds) => (
  clientNeedIds?.length > 0 ?
    clientNeedIds.map((id) => ({ type: 'person_condition', id })) :
    undefined
);

const buildCaseData = (params, groupId, contactId, isOON, personConditions) => ({
  program_enrollment_date: params.program.entry_date,
  description: params.service_case.description,
  state: getCaseState(isOON, params),
  provider: groupId,
  network: params.network_id,
  primary_worker: params.service_case.primary_worker_id,
  person: contactId,
  service: params.service_type_id,
  ...(isOON ? {} : {
    opened_date: moment.unix(params.program.entry_date).utc().format('YYYY-MM-DD'),
    program: params.program.program_id,
  }),
  ...(personConditions ? { person_conditions: personConditions } : {}),
});

const createCase = async (params, groupId, contactId, isOONCase, clientNeedIds, state) => {
  const isOON = isOONCase || params.program.program_name === 'Referred Out of Network';
  const multipleClientNeedsFlag = crtb2130MultipleClientNeeds(state);
  let personConditions;
  if (multipleClientNeedsFlag) {
    personConditions = buildPersonConditions(clientNeedIds);
  } else {
    personConditions = clientNeedIds ? [{ type: 'person_condition', id: clientNeedIds }] : undefined;
  }
  const caseData = buildCaseData(params, groupId, contactId, isOON, personConditions);

  return coreApi.createRecord('case', caseData);
};

const handleAuthorizationRequest = async (params, contactId, caseId, state) => {
  const authorizationRequest = parseAuthorizationParams(params);
  const feeScheduleProgramId = get(params, 'program.fee_schedule_program_id', null);

  // PAYS-10723
  datadogLogs.logger.info('handleAuthorizationRequest called', {
    caseId,
    feeScheduleProgramId,
    authorizationRequest,
    rawAuthRequest: get(params, 'authorization_request'),
    isEmpty: isEmpty(authorizationRequest),
    willCreateAuth: !!(feeScheduleProgramId && !isEmpty(authorizationRequest)),
  });

  if (feeScheduleProgramId && !isEmpty(authorizationRequest)) {
    const {
      service_start: serviceStart,
      service_end: serviceEnd,
      amount_requested: amountRequested,
      insurance_id: insuranceId,
    } = authorizationRequest;

    const fspResponse = await coreApi.findRecord('fee_schedule_program', feeScheduleProgramId);
    const feeScheduleProgram = get(fspResponse, 'data.data');
    if (!feeScheduleProgram) {
      throw new Error('Contracted Program required');
    }
    const isCostBased = fspUtils.isCostPaymentType(feeScheduleProgram.payment_type);

    // PAYS-10723
    datadogLogs.logger.info('handleAuthorizationRequest: Creating service authorization', {
      insuranceId,
      caseId,
    });
    await coreApi.createRecord('service_authorization', {
      state: 'requested',
      requested_starts_at: iso8601Date(serviceStart),
      requested_ends_at: iso8601Date(serviceEnd || serviceStart),
      ...(isCostBased ? { requested_cents: amountRequested } : { requested_unit_amount: amountRequested }),
      case: caseId,
      fee_schedule_program: feeScheduleProgramId,
      person: contactId,
      requester: get(state, 'globalState.currentEmployee.id'),
      insurance: insuranceId,
    });
  }
};

const uploadDocuments = async (caseId, dropzoneDocuments, attachedDocuments) => {
  await Promise.all([
    ...(dropzoneDocuments.length ? dropzoneDocuments.map((doc) => coreApi.uploadFile('case', caseId, doc)) : []),
    ...(attachedDocuments.length ? [
      coreApi.setRelationship(
        'case',
        caseId,
        { relationshipModel: 'file_upload', value: attachedDocuments.map((a) => a.document_id) },
      ),
    ] : []),
  ]);
};

const handleOONProviders = async (oonProviders, createdCase, caseId) => {
  await Promise.all(oonProviders
    .filter((oonProvider) => oonProvider.provider_type !== 'CUSTOM')
    .map((oonProvider) => coreApi.createRecord('referral', {
      state: 'off_platform',
      case: caseId,
      sending_provider: createdCase.provider.id,
      sending_network: createdCase.network.id,
      receiving_provider: oonProvider.provider_id,
      receiving_network: createdCase.network.id,
      ...(oonProvider.program_id ? { receiving_program: oonProvider.program_id } : null),
    })));
};

const createCustomProviderNotes = async (oonProviders, caseId, currentEmployee) => {
  await Promise.all(oonProviders
    .filter((oonProvider) => oonProvider.provider_type === 'CUSTOM' && !isEmpty(oonProvider.custom_name))
    .map((oonProvider) => coreApi.createRecord('note', {
      subjectType: 'case',
      subject: caseId,
      employee: currentEmployee.id,
      visibility: 'public',
      category: 'external_provider',
      text: `Referred off platform to ${oonProvider.custom_name}`,
    })));
};

export default function createServiceCase({
  attachedDocuments = [],
  contactId,
  dropzoneDocuments = [],
  groupId,
  isOONCase = false,
  params = {},
  clientNeedIds = [],
}) {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: CREATE_SERVICE_CASE_SET_SUBMITTING });

      const state = getState();

      // Validate insurance_id is present if authorization will be created
      const authorizationRequest = parseAuthorizationParams(params);
      const feeScheduleProgramId = get(params, 'program.fee_schedule_program_id', null);
      const willCreateAuthorization = !!(feeScheduleProgramId && !isEmpty(authorizationRequest));

      if (willCreateAuthorization && !authorizationRequest.insurance_id) {
        // PAYS-10723
        datadogLogs.logger.error('createServiceCase: Missing insurance_id for authorization', {
          authorizationRequest,
          feeScheduleProgramId,
        });
        throw new Error('Insurance is required when creating a case with authorization');
      }

      // PAYS-10723
      datadogLogs.logger.info('createServiceCase: Starting case creation', {
        contactId,
        groupId,
        isOONCase,
        hasAuthRequest: !!get(params, 'authorization_request'),
        hasFeeScheduleProgram: !!feeScheduleProgramId,
        willCreateAuthorization,
      });

      const payload = await createCase(params, groupId, contactId, isOONCase, clientNeedIds, state);
      const contact = await coreApi.populateRelationship('person', 'person', payload.data.data);
      await coreApi.populateRelationship('consent', 'consent', contact.person);

      dispatch({ type: CREATE_SERVICE_CASE_UNSET_SUBMITTING });
      dispatch({ type: CREATE_SERVICE_CASE, payload });
      Notifier.dispatch(payload.status, 'Case Created');

      const createdCase = payload.data.data;
      const { id: caseId } = createdCase;

      // PAYS-10723
      datadogLogs.logger.info('createServiceCase: Case created successfully, calling handleAuthorizationRequest', {
        caseId,
      });

      await handleAuthorizationRequest(params, contactId, caseId, state);
      await uploadDocuments(caseId, dropzoneDocuments, attachedDocuments);
      const oonProviders = get(params, 'service_case.out_of_network_providers', []);
      await handleOONProviders(oonProviders, createdCase, caseId);
      await createCustomProviderNotes(oonProviders, caseId, get(state, 'globalState.currentEmployee'));

      return payload;
    } catch (error) {
      // PAYS-10723
      datadogLogs.logger.error('createServiceCase: Error occurred when creating case: ', error);
      dispatch({ type: CREATE_SERVICE_CASE_UNSET_SUBMITTING });
      Notifier.handleErrors(error);
      return error;
    }
  };
}
